// Lab Approvals Service — Postgres via Azure Function (lab_approvals)
import { authService } from '../auth/authService';

class LabApprovalsService {
  constructor() {
    this.functionAppUrl = 'https://wolfwalker-api-b7abdbh3apbdavga.canadacentral-01.azurewebsites.net';
    this.approvalsApiUrl = `${this.functionAppUrl}/api/lab_approvals`;
  }

  async getAuthenticatedHeaders() {
    try {
      const token = await authService.getAccessToken();
      const userInfo = authService.getUserInfo();

      return {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-user-id': userInfo?.email || userInfo?.username || 'unknown',
      };
    } catch (error) {
      console.error('Failed to get auth headers:', error);
      throw new Error('Authentication failed');
    }
  }

  /**
   * @param {{ limit?: number, offset?: number, status?: string, search?: string }} options
   */
  /**
   * COUNT(*) from als_raw.mvw_results_pivot for one workorder (samples in results DB).
   * GET ?sample_count_only=true&workorder_id=WH...
   */
  async getResultsSampleCount(workorderId) {
    const wid = (workorderId || '').trim();
    if (!wid) {
      throw new Error('workorder_id is required');
    }
    const headers = await this.getAuthenticatedHeaders();
    const params = new URLSearchParams({
      sample_count_only: 'true',
      workorder_id: wid,
    });
    const response = await fetch(`${this.approvalsApiUrl}?${params}`, {
      method: 'GET',
      headers,
    });
    if (!response.ok) {
      const errText = await response.text();
      throw new Error(errText || `Sample count failed: ${response.statusText}`);
    }
    const result = await response.json();
    if (result.success === false) {
      throw new Error(result.message || result.error || 'Sample count failed');
    }
    const n = result.data?.samples_sent_shipped_from_results;
    return typeof n === 'number' ? n : Number(n) || 0;
  }

  /**
   * Pivot sample count, QAQC DB note, and minto_db news release rows for one workorder.
   * GET ?workorder_meta=true&workorder_id=WH...
   */
  async getWorkorderMeta(workorderId) {
    const wid = (workorderId || '').trim();
    if (!wid) {
      throw new Error('workorder_id is required');
    }
    const headers = await this.getAuthenticatedHeaders();
    const params = new URLSearchParams({
      workorder_meta: 'true',
      workorder_id: wid,
    });
    const response = await fetch(`${this.approvalsApiUrl}?${params}`, {
      method: 'GET',
      headers,
    });
    if (!response.ok) {
      const errText = await response.text();
      throw new Error(errText || `Workorder meta failed: ${response.statusText}`);
    }
    const result = await response.json();
    if (result.success === false) {
      throw new Error(result.message || result.error || 'Workorder meta failed');
    }
    return result.data || {};
  }

  async getApprovals(options = {}) {
    const {
      limit = 100_000,
      offset = 0,
      status = 'all',
      search = '',
    } = options;

    const headers = await this.getAuthenticatedHeaders();
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    });
    if (status && status !== 'all') {
      params.set('status', status);
    }
    if (search && search.trim()) {
      params.set('search', search.trim());
    }

    const response = await fetch(`${this.approvalsApiUrl}?${params}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(errText || `Failed to fetch lab approvals: ${response.statusText}`);
    }

    const result = await response.json();
    if (result.success === false) {
      throw new Error(result.message || result.error || 'Lab approvals request failed');
    }

    return {
      success: true,
      data: result.data || [],
      message: result.message,
    };
  }

  /**
   * Full save / upsert for als_approvals.workorder_approvals
   */
  /**
   * Extract workorder_approvals fields from an invoice image or PDF (OpenAI on server).
   * PDFs: sent natively via OpenAI Responses API (input_file), not rasterized in Wolfwalker.
   * @param {{ imageBase64: string, mimeType?: string }} params
   * @returns {Promise<{ data: object, meta?: { model?: string } }>}
   */
  async extractFromImage({ imageBase64, mimeType = 'image/png' }) {
    const headers = await this.getAuthenticatedHeaders();
    const response = await fetch(this.approvalsApiUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        action: 'extract_from_image',
        image_base64: imageBase64,
        mime_type: mimeType,
      }),
    });

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(errText || `Extract failed: ${response.statusText}`);
    }

    const result = await response.json();
    if (result.success === false) {
      throw new Error(result.message || result.error || 'Extract failed');
    }

    return {
      data: result.data || {},
      meta: result.meta || {},
    };
  }

  async saveWorkorderApproval(payload) {
    const headers = await this.getAuthenticatedHeaders();
    const body = { action: 'save', ...payload };

    const response = await fetch(this.approvalsApiUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(errText || `Failed to save: ${response.statusText}`);
    }

    const result = await response.json();
    if (result.success === false) {
      throw new Error(result.message || result.error || 'Save failed');
    }

    return {
      success: true,
      message: result.message,
      data: result.data,
    };
  }

  /** Save approval fields and set approved=true in one request (when QAQC passed). */
  async saveAndApproveWorkorder(payload) {
    const headers = await this.getAuthenticatedHeaders();
    const body = { action: 'save_and_approve', ...payload };

    const response = await fetch(this.approvalsApiUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(errText || `Failed to save and approve: ${response.statusText}`);
    }

    const result = await response.json();
    if (result.success === false) {
      throw new Error(result.message || result.error || 'Save and approve failed');
    }

    return {
      success: true,
      message: result.message,
      data: result.data,
    };
  }

  async approveWorkorder(workorderId, comments = '') {
    const headers = await this.getAuthenticatedHeaders();

    const response = await fetch(this.approvalsApiUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        action: 'approve',
        workorder_id: workorderId,
        comments,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to approve workorder: ${response.statusText}`);
    }

    const result = await response.json();
    if (result.success === false) {
      throw new Error(result.message || result.error || 'Approve failed');
    }

    return {
      success: result.success,
      message: result.message,
      data: result.data,
    };
  }

  async rejectWorkorder(workorderId, reason) {
    if (!reason || reason.trim() === '') {
      throw new Error('Rejection reason is required');
    }

    const headers = await this.getAuthenticatedHeaders();

    const response = await fetch(this.approvalsApiUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        action: 'reject',
        workorder_id: workorderId,
        reason,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to reject workorder: ${response.statusText}`);
    }

    const result = await response.json();
    if (result.success === false) {
      throw new Error(result.message || result.error || 'Reject failed');
    }

    return {
      success: result.success,
      message: result.message,
      data: result.data,
    };
  }

  async getDashboardData(options = {}) {
    const approvalsData = await this.getApprovals({
      limit: options.limit ?? 100_000,
      offset: options.offset ?? 0,
      status: options.status ?? 'all',
      search: options.search ?? '',
    });

    return {
      approvalsData: approvalsData.data,
      success: true,
    };
  }

  filterData(data, searchTerm) {
    if (!searchTerm) return data;
    const term = searchTerm.toLowerCase();
    return data.filter((item) => JSON.stringify(item).toLowerCase().includes(term));
  }

  sortData(data, field, direction = 'asc') {
    return [...data].sort((a, b) => {
      const aVal = a[field] ?? '';
      const bVal = b[field] ?? '';

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      if (direction === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });
  }
}

export const labApprovalsService = new LabApprovalsService();
export default labApprovalsService;
